import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import {
  BILLING_INTERVAL_LABEL,
  BILLING_INTERVAL_OPTIONS,
  OFFER_TYPE_LABEL,
  OFFER_TYPE_OPTIONS,
  PRICE_AMOUNT_MAX,
} from '../../../../../Consts/plans.const';
import {
  PlanPriceDialogData,
  PlanPriceDialogResult,
} from '../../../Interfaces/plan-dialog.interface';

@Component({
  selector: 'app-plan-price-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './plan-price-dialog.component.html',
  styleUrl: './plan-price-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanPriceDialogComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<PlanPriceDialogComponent, PlanPriceDialogResult>>(MatDialogRef);

  readonly data = inject<PlanPriceDialogData>(MAT_DIALOG_DATA);

  readonly offerOptions = OFFER_TYPE_OPTIONS;
  readonly offerLabelKeys = OFFER_TYPE_LABEL;
  readonly intervalOptions = BILLING_INTERVAL_OPTIONS;
  readonly intervalLabelKeys = BILLING_INTERVAL_LABEL;
  readonly amountMax = PRICE_AMOUNT_MAX;

  readonly permittedComponents = this.data.components.filter(
    (component) => component.is_permitted,
  );

  readonly isEdit = this.data.price !== null;

  readonly formGroup: FormGroup = this._formBuilder.group({
    component_type: [
      this.data.price?.component_type ?? this.permittedComponents[0]?.component_type ?? '',
      [Validators.required],
    ],
    offer_type: [this.data.price?.offer_type ?? OFFER_TYPE_OPTIONS[0], [Validators.required]],
    billing_interval: [
      this.data.price?.billing_interval ?? BILLING_INTERVAL_OPTIONS[0],
      [Validators.required],
    ],
    currency: [
      this.data.price?.currency ?? this.data.defaultCurrency ?? '',
      [Validators.required, Validators.minLength(3), Validators.maxLength(3)],
    ],
    unit_amount: [
      this.data.price?.unit_amount ?? null,
      [Validators.required, Validators.min(0.01), Validators.max(PRICE_AMOUNT_MAX)],
    ],
    is_active: [this.data.price?.is_active ?? true],
  });

  confirm(): void {
    if (!this.formGroup.valid) {
      return;
    }

    this._dialogRef.close({
      componentType: String(this.formGroup.value.component_type),
      offerType: String(this.formGroup.value.offer_type),
      billingInterval: String(this.formGroup.value.billing_interval),
      currency: String(this.formGroup.value.currency).toUpperCase(),
      unitAmount: Number(this.formGroup.value.unit_amount),
      isActive: Boolean(this.formGroup.value.is_active),
    });
  }

  cancel(): void {
    this._dialogRef.close();
  }
}
